import type { Animation, Direction8 } from "../schema/timeline";
import type { TargetState } from "./state";
import { iterationsOf } from "./compile";
import { sampleMotionPath } from "./motionPath";

export interface Keyframe {
  offset?: number;
  [prop: string]: string | number | undefined;
}

export interface KeyframeOptions {
  duration: number;
  delay: number;
  easing: string;
  iterations: number;
  direction: "normal" | "alternate";
  fill: "forwards" | "none" | "both";
}

/** Serializable description of one Web Animations call. */
export interface KeyframeSpec {
  /** which DOM node to animate: the element wrapper or (for text scope) the paragraph nodes */
  keyframes: Keyframe[];
  options: KeyframeOptions;
  /** when true, the engine makes the wrapper visible before starting (entrances) */
  showFirst?: boolean;
  /** when true, the engine hides the wrapper after finishing (exits) */
  hideAfter?: boolean;
}

export interface KeyframeEnv {
  /** axis-aligned bounds of the target in slide pt */
  bbox: { x: number; y: number; w: number; h: number };
  slide: { w: number; h: number };
  /** resting state before this animation starts */
  startState: TargetState;
}

const BOUNCE = "linear(0, 0.004, 0.016, 0.035, 0.063, 0.098, 0.141, 0.191, 0.25, 0.316, 0.391, 0.473, 0.563, 0.66, 0.766, 0.879, 1, 0.922, 0.863, 0.822, 0.801, 0.797, 0.813, 0.848, 0.902, 0.975, 1, 0.967, 0.95, 0.953, 0.976, 1, 0.988, 0.984, 0.988, 1)";

function easingFor(anim: Animation): string {
  const { accel, decel } = anim.easing;
  if (accel > 0 && decel > 0) return "ease-in-out";
  if (accel > 0) return "ease-in";
  if (decel > 0) return "ease-out";
  const e = anim.effect;
  if (e.kind === "fly" || e.kind === "float" || e.kind === "zoom") return "ease-out";
  return "linear";
}

function baseTransform(s: TargetState): string {
  return `translate(${s.translate.x}px, ${s.translate.y}px) rotate(${s.rotate}deg) scale(${s.scale.x}, ${s.scale.y})`;
}

function flyOffset(dir: Direction8, env: KeyframeEnv): { x: number; y: number } {
  const { bbox, slide } = env;
  const left = -(bbox.x + bbox.w);
  const right = slide.w - bbox.x;
  const top = -(bbox.y + bbox.h);
  const bottom = slide.h - bbox.y;
  switch (dir) {
    case "left": return { x: left, y: 0 };
    case "right": return { x: right, y: 0 };
    case "top": return { x: 0, y: top };
    case "bottom": return { x: 0, y: bottom };
    case "topLeft": return { x: left, y: top };
    case "topRight": return { x: right, y: top };
    case "bottomLeft": return { x: left, y: bottom };
    case "bottomRight": return { x: right, y: bottom };
  }
}

function withPhase(frames: Keyframe[], phase: "in" | "out"): Keyframe[] {
  if (phase === "in") return frames;
  const reversed = [...frames].reverse().map((f) => {
    const { offset, ...rest } = f;
    return offset === undefined ? rest : { ...rest, offset: 1 - offset };
  });
  return reversed;
}

/**
 * Produce Web Animations keyframes for one animation. All transforms are composed on top of the
 * target's resting transform (`startState`) so persisted emphasis effects are honored.
 */
export function keyframesFor(anim: Animation, env: KeyframeEnv): KeyframeSpec[] {
  const s = env.startState;
  const base = baseTransform(s);
  const opts: KeyframeOptions = {
    duration: Math.max(1, anim.duration),
    delay: 0,
    easing: easingFor(anim),
    iterations: iterationsOf(anim),
    direction: anim.autoReverse ? "alternate" : "normal",
    fill: "forwards",
  };
  const e = anim.effect;
  const phase = "phase" in e && (e.phase === "in" || e.phase === "out") ? e.phase : null;
  const flags = phase === "in" ? { showFirst: true } : phase === "out" ? { hideAfter: true } : {};
  const one = (frames: Keyframe[], extra: Partial<KeyframeOptions> = {}): KeyframeSpec[] => [
    { keyframes: phase ? withPhase(frames, phase) : frames, options: { ...opts, ...extra }, ...flags },
  ];

  switch (e.kind) {
    case "appear":
      return one([{ opacity: 0, offset: 0 }, { opacity: 0, offset: 0.999 }, { opacity: s.opacity, offset: 1 }], { duration: 1, easing: "linear" });
    case "fade":
      return one([{ opacity: 0 }, { opacity: s.opacity }]);
    case "fly": {
      const o = flyOffset(e.direction, env);
      return one([
        { transform: `translate(${o.x}px, ${o.y}px) ${base}` },
        { transform: base },
      ]);
    }
    case "float": {
      const dy = e.direction === "up" ? env.slide.h * 0.2 : -env.slide.h * 0.2;
      return one([
        { transform: `translate(0px, ${dy}px) ${base}`, opacity: 0 },
        { transform: base, opacity: s.opacity },
      ]);
    }
    case "wipe": {
      const from = { left: "inset(0 100% 0 0)", right: "inset(0 0 0 100%)", top: "inset(0 0 100% 0)", bottom: "inset(100% 0 0 0)" }[e.direction];
      return one([{ clipPath: from }, { clipPath: "inset(0 0 0 0)" }]);
    }
    case "split": {
      const closed = e.orientation === "vertical" ? "inset(0 50% 0 50%)" : "inset(50% 0 50% 0)";
      const open = "inset(0 0 0 0)";
      const frames = e.mode === "in" ? [{ clipPath: closed }, { clipPath: open }] : [{ clipPath: open }, { clipPath: closed }];
      // "out" mode of an entrance reveals from the outside in, which inset() cannot express; approximate.
      return one(e.mode === "in" ? frames : [{ clipPath: closed }, { clipPath: open }]);
    }
    case "zoom": {
      const from = e.fromScale;
      if (e.origin === "slide") {
        const cx = env.slide.w / 2 - (env.bbox.x + env.bbox.w / 2);
        const cy = env.slide.h / 2 - (env.bbox.y + env.bbox.h / 2);
        return one([
          { transform: `translate(${cx}px, ${cy}px) scale(${from}) ${base}`, opacity: 0 },
          { transform: base, opacity: s.opacity },
        ]);
      }
      return one([
        { transform: `scale(${from}) ${base}`, opacity: from < 1 ? 0 : s.opacity },
        { transform: base, opacity: s.opacity },
      ]);
    }
    case "stretch": {
      const origin = { left: "left center", right: "right center", top: "center top", bottom: "center bottom", across: "center center" }[e.direction];
      const sc = e.direction === "top" || e.direction === "bottom" ? "scaleY(0)" : "scaleX(0)";
      return one([
        { transform: `${sc} ${base}`, transformOrigin: origin },
        { transform: base, transformOrigin: origin },
      ]);
    }
    case "peek": {
      const d = { left: { x: -env.bbox.w, y: 0 }, right: { x: env.bbox.w, y: 0 }, top: { x: 0, y: -env.bbox.h }, bottom: { x: 0, y: env.bbox.h } }[e.direction];
      const clip = { left: "inset(0 0 0 100%)", right: "inset(0 100% 0 0)", top: "inset(0 0 100% 0)", bottom: "inset(100% 0 0 0)" }[e.direction];
      return one([
        { transform: `translate(${d.x}px, ${d.y}px) ${base}`, clipPath: clip },
        { transform: base, clipPath: "inset(0 0 0 0)" },
      ]);
    }
    case "bounce": {
      const o = flyOffset("bottomLeft", env);
      return one([
        { transform: `translate(${o.x}px, ${o.y}px) ${base}`, opacity: 0 },
        { transform: `translate(${o.x * 0.05}px, ${o.y * 0.05}px) ${base}`, opacity: s.opacity, offset: 0.1 },
        { transform: base, opacity: s.opacity },
      ], { easing: BOUNCE });
    }
    case "wheel":
      return one([{ opacity: 0 }, { opacity: s.opacity }]);
    case "growShrink": {
      const from = `translate(${s.translate.x}px, ${s.translate.y}px) rotate(${s.rotate}deg) scale(${s.scale.x}, ${s.scale.y})`;
      const to = `translate(${s.translate.x}px, ${s.translate.y}px) rotate(${s.rotate}deg) scale(${s.scale.x * e.scaleX}, ${s.scale.y * e.scaleY})`;
      return one([{ transform: from }, { transform: to }]);
    }
    case "spin": {
      const to = `translate(${s.translate.x}px, ${s.translate.y}px) rotate(${s.rotate + e.degrees}deg) scale(${s.scale.x}, ${s.scale.y})`;
      return one([{ transform: base }, { transform: to }]);
    }
    case "pulse":
      return one([
        { transform: base },
        { transform: `scale(1.1) ${base}`, offset: 0.5 },
        { transform: base },
      ], { fill: "none" });
    case "teeter":
      return one([
        { transform: base },
        { transform: `rotate(-5deg) ${base}`, offset: 0.25 },
        { transform: `rotate(5deg) ${base}`, offset: 0.75 },
        { transform: base },
      ], { fill: "none" });
    case "transparency":
      return one([{ opacity: s.opacity }, { opacity: e.opacity }]);
    case "colorChange": {
      const prop = e.property === "fill" ? "fill" : e.property === "line" ? "stroke" : "color";
      return [{ keyframes: [{ [prop]: e.to.hex }], options: { ...opts, fill: e.persist ? "forwards" : "none" } }];
    }
    case "boldFlash":
    case "underline":
      return one([
        { transform: base },
        { transform: `scale(1.05) ${base}`, offset: 0.5 },
        { transform: base },
      ], { fill: "none" });
    case "motionPath": {
      const samples = sampleMotionPath(e.path);
      const frames: Keyframe[] = samples.map((p) => ({
        offset: p.t,
        transform: `translate(${s.translate.x + p.x}px, ${s.translate.y + p.y}px) rotate(${s.rotate + (e.rotateAlongPath ? p.angle : 0)}deg) scale(${s.scale.x}, ${s.scale.y})`,
      }));
      return one(frames, { easing: "linear" });
    }
    case "media":
      return [];
    case "fallback":
      if (e.phase === "emph") {
        return one([{ transform: base }, { transform: `scale(1.08) ${base}`, offset: 0.5 }, { transform: base }], { fill: "none" });
      }
      return [{
        keyframes: withPhase([{ opacity: 0 }, { opacity: s.opacity }], e.phase),
        options: opts,
        ...(e.phase === "in" ? { showFirst: true } : { hideAfter: true }),
      }];
  }
}
